/**
 * Production — what the network has asked for against what Autozone already
 * holds, and so what the factory still has to build (owner, 25 Sep 2026:
 * "Expanted view of orders for production analysis that what we have in
 * stock and what we need to produce. Also the bifercation of stock varient
 * vise with count of colors in same row").
 *
 * Orders and stock do not speak the same words. An order names one of the
 * order form's nine models ("CC E-Max DLX (L5)"); a vehicle in the yard is
 * named by its chassis code ("CITY CAB E-MAX") and, until the dispatch form
 * fills them in, carries no trim, colour or battery at all — the factory
 * register records the model and nothing else. So the one level both sides
 * can be counted on is the chassis FAMILY: the Super, the Cargo, the E-Max,
 * the Pro. Demand is netted against stock there; finer than that (the trim,
 * the colour) is shown where the book knows it and said to be unknown where
 * it does not, never guessed.
 */

import { CATALOGUE_MODELS, modelFromLabel } from './catalogue.js';
import { DISPATCH_MODEL_TYPES } from './dispatch-form.js';
import { batteryAhFrom, batteryRung } from './sale.js';

export interface ProductionFamily {
  key: string;
  /** How the chassis code names it — the spelling stock carries. */
  label: string;
  /** The order form's models that are built on this chassis. */
  catalogueKeys: readonly string[];
}

/**
 * The chassis families, in the order the office thinks of them. The first
 * four are what the order form sells; City Auto, Garib Rath and the City Cab
 * EV are older builds the order form no longer offers — stock of them is
 * shown, with nothing asked for.
 */
export const PRODUCTION_FAMILIES: readonly ProductionFamily[] = [
  { key: 'SUPER', label: 'CITY CAB SUPER', catalogueKeys: ['CC_SUPER_ECO', 'CC_SUPER_DLX'] },
  { key: 'CARGO', label: 'CITY CAB CARGO', catalogueKeys: ['CC_JUMBO', 'CC_MINI_CARGO', 'CC_DUST_CART', 'CC_CARGO_CUSTOMISED', 'CC_CARGO_SUPER_CUSTOMISED'] },
  { key: 'EMAX', label: 'CITY CAB E-MAX', catalogueKeys: ['CC_EMAX_ECO', 'CC_EMAX_DLX'] },
  { key: 'PRO', label: 'CITY CAB PRO', catalogueKeys: ['CC_PRO'] },
  { key: 'AUTO', label: 'CITY AUTO', catalogueKeys: [] },
  { key: 'GARIB', label: 'GARIB RATH', catalogueKeys: [] },
  { key: 'EV', label: 'CITY CAB EV', catalogueKeys: [] },
];

/** The key a row that fits no family carries. */
export const OTHER_FAMILY = 'OTHER';

const squash = (text: string | null | undefined) => (text ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/** The family an order line's model belongs to, or OTHER when the catalogue does not know it. */
export function familyForOrderModel(label: string | null | undefined): string {
  const model = modelFromLabel(label);
  if (!model) return OTHER_FAMILY;
  return PRODUCTION_FAMILIES.find((family) => family.catalogueKeys.includes(model.key))?.key ?? OTHER_FAMILY;
}

/**
 * The family a vehicle's model belongs to, however the sheet spelt it
 * ("CITYCAB CARGO", "City Cab E Max", "CITY CAB SUPER DLX").
 */
export function familyForStockModel(model: string | null | undefined): string {
  const m = squash(model);
  if (!m) return OTHER_FAMILY;
  if (m.includes('EMAX')) return 'EMAX';
  if (m.includes('CARGO') || m.includes('JUMBO') || m.includes('LOADER')) return 'CARGO';
  if (m.includes('SUPER')) return 'SUPER';
  if (/CITYCABPRO|^CCPRO/.test(m)) return 'PRO';
  if (m.includes('CITYAUTO')) return 'AUTO';
  if (m.includes('GARIB')) return 'GARIB';
  if (m.includes('CITYCABEV') || m === 'EV') return 'EV';
  return OTHER_FAMILY;
}

/** The order form's label for a catalogue key ("CC E-Max DLX"). */
export function catalogueLabel(key: string): string {
  return CATALOGUE_MODELS.find((model) => model.key === key)?.label ?? key;
}

// ---------------------------------------------------------------------------
// Model and variant, in the factory's words
// ---------------------------------------------------------------------------
//
// Owner, 12 Oct 2026: "we dont have that many model and varient … explain
// varient properly". The first plan counted every order-form line × battery
// size × colour as its own thing to build, and an order written from the old
// dispatch form ("CITY CAB SUPER" + "DLX Li") as a model of its own, so one
// vehicle read as a dozen.
//
// The office's own words settle it. A MODEL is the vehicle: the Super, the
// E-Max, the Pro, and the cargo bodies. A VARIANT is the version of it the
// factory builds, as the dispatch form's "Type of model" names it: ECO or
// DLX, with "Li" when it takes a lithium battery ("2 new variants are the e
// max eco + and e max dlx +", 17 Sep). The battery's SIZE is not a variant —
// "same variant is sent by 132ah battery" (18 Sep) — so it is carried beside
// the line, never split on.

/** What an order says about the battery it is built for. */
export type BatteryKind = 'LI' | 'LEAD' | 'NONE' | 'UNSAID';

const BATTERY_KIND_ORDER: readonly BatteryKind[] = ['LI', 'LEAD', 'NONE', 'UNSAID'];
const TRIM_ORDER = ['ECO', 'DLX', ''];

/** "Lithium-ion 105 Ah" → LI, "Lead acid 140 Ah" → LEAD, "Without battery" → NONE; the old form's "DLX Li" → LI, its plain "DLX" → LEAD. */
export function batteryKindOf(text: string | null | undefined): BatteryKind {
  const spec = (text ?? '').trim();
  if (!spec) return 'UNSAID';
  if (/without|no\s+battery/i.test(spec)) return 'NONE';
  if (/lith|li-?ion|\bli\b/i.test(spec)) return 'LI';
  if (/lead/i.test(spec)) return 'LEAD';
  // The dispatch form writes a lead-acid build as the bare trim: "DLX", "ECO".
  if (/^(MINI\s+)?(ECO|DLX)\s*\+?$/i.test(spec)) return 'LEAD';
  return 'UNSAID';
}

/** The size an order asked for — "105 Ah" — or what stands in for it. */
export function batterySizeOf(text: string | null | undefined): string {
  const kind = batteryKindOf(text);
  if (kind === 'NONE') return 'No battery';
  const size = /(\d{2,3})\s*AH\b/i.exec(text ?? '');
  if (size) return `${Number(size[1])} Ah`;
  return kind === 'UNSAID' ? 'Not said' : 'Size not said';
}

/** "DLX Li", "ECO (lead acid)", "Li", "No battery" — the variant as the plan prints it. */
export function variantLabel(trim: string | null, kind: BatteryKind): string {
  if (trim) {
    if (kind === 'LI') return `${trim} Li`;
    if (kind === 'LEAD') return `${trim} (lead acid)`;
    if (kind === 'NONE') return `${trim}, no battery`;
    return `${trim}, battery not said`;
  }
  return { LI: 'Li', LEAD: 'Lead acid', NONE: 'No battery', UNSAID: 'Battery not said' }[kind];
}

/** One thing the factory builds, read off an order line. */
export interface OrderProduct {
  family: string;
  /** Stable: the catalogue key with its ECO / DLX taken off ("CC_SUPER"), or the line as written. */
  modelKey: string;
  /** "CC Super". */
  model: string;
  /** Stable: the trim and the battery kind ("DLX|LI"). */
  variantKey: string;
  /** "DLX Li". */
  variant: string;
  /** "105 Ah", "No battery", "Not said". */
  batterySize: string;
}

/** The catalogue's models with their ECO / DLX taken off, in its own order: the Super, the E-Max, the Pro… */
const BASE_MODELS = (() => {
  const seen = new Map<string, { key: string; label: string; family: string }>();
  for (const model of CATALOGUE_MODELS) {
    const key = model.key.replace(/_(ECO|DLX)$/, '');
    if (seen.has(key)) continue;
    const family = PRODUCTION_FAMILIES.find((f) => f.catalogueKeys.includes(model.key))?.key ?? OTHER_FAMILY;
    seen.set(key, { key, label: model.label.replace(/\s+(Eco|DLX)$/i, ''), family });
  }
  return [...seen.values()];
})();

/** Where a model sits in the plan's order — the catalogue's, then the older builds, then the rest. */
export function modelRank(modelKey: string): number {
  const at = BASE_MODELS.findIndex((m) => m.key === modelKey);
  if (at >= 0) return at;
  const family = PRODUCTION_FAMILIES.findIndex((f) => f.key === modelKey);
  return family >= 0 ? BASE_MODELS.length + family : BASE_MODELS.length + PRODUCTION_FAMILIES.length;
}

/** Where a variant sits within its model: ECO before DLX, lithium before lead, no battery after. */
export function variantRank(variantKey: string): number {
  const [trim = '', kind = 'UNSAID'] = variantKey.split('|');
  return TRIM_ORDER.indexOf(trim) * 10 + BATTERY_KIND_ORDER.indexOf(kind as BatteryKind);
}

/** "CITY AUTO" → "City Auto", "CITY CAB EV" → "City Cab EV". */
const titled = (text: string) =>
  text.toLowerCase().replace(/(^|[\s-])(\w)/g, (_m, lead: string, ch: string) => lead + ch.toUpperCase()).replace(/\bEv\b/g, 'EV');

/**
 * The Pro's ECO or DLX, which the order form does not ask: on the Pro the
 * trim is the battery (owner, 12 Oct 2026: "it should be eco Li because it
 * has 105ah battery" — `variantByBattery`). A lithium battery of the 105
 * class is the ECO Li, a bigger one the DLX Li; no size said, no guess.
 */
function proTrimOf(kind: BatteryKind, batteryText: string | null | undefined): string | null {
  if (kind !== 'LI') return null;
  const ah = batteryAhFrom(batteryText);
  if (ah === null) return null;
  return batteryRung(ah) === 105 ? 'ECO' : 'DLX';
}

/**
 * The model and variant an order line asks for. The order form's own lines
 * ("CC Super DLX (L3)" + "Lithium-ion 105 Ah") read straight off the
 * catalogue. A line written from the old dispatch form ("CITY CAB SUPER" +
 * "DLX Li", the 1–8 Sep backfill) is folded into the same model by its
 * chassis family, so one vehicle is never two models. The E-Max's "+" is its
 * motor (21 Sep), which the order form does not ask, so it is not split on.
 */
export function orderProductOf(modelText: string | null | undefined, batteryText: string | null | undefined): OrderProduct {
  const text = (modelText ?? '').trim();
  const kind = batteryKindOf(batteryText);
  const batterySize = batterySizeOf(batteryText);
  const product = (family: string, modelKey: string, model: string, trim: string | null): OrderProduct => ({
    family,
    modelKey,
    model,
    variantKey: `${trim ?? ''}|${kind}`,
    variant: variantLabel(trim, kind),
    batterySize,
  });

  const known = modelFromLabel(text);
  if (known) {
    const base = BASE_MODELS.find((m) => m.key === known.key.replace(/_(ECO|DLX)$/, ''))!;
    const trim = /_(ECO|DLX)$/.exec(known.key)?.[1] ?? (base.family === 'PRO' ? proTrimOf(kind, batteryText) : null);
    return product(base.family, base.key, base.label, trim);
  }
  if (!text) return product(OTHER_FAMILY, '', 'Model not said', null);

  const family = familyForStockModel(text);
  const upper = text.toUpperCase();
  if (family === 'CARGO') {
    // The body, where the line names it ("MINI ECO Li" is the Mini Cargo's); the cargo chassis alone where it does not.
    const said = `${upper} ${(batteryText ?? '').toUpperCase()}`;
    const body =
      (/JUMBO/.test(said) && 'CC_JUMBO') || (/MINI/.test(said) && 'CC_MINI_CARGO') || (/DUST/.test(said) && 'CC_DUST_CART') || null;
    const base = BASE_MODELS.find((m) => m.key === body);
    return base ? product('CARGO', base.key, base.label, null) : product('CARGO', 'CARGO', 'Cargo, body not said', null);
  }
  const base = BASE_MODELS.find((m) => m.family === family);
  if (base) {
    // A trim where the order form asks one — the Super and the E-Max — and the Pro's, read off its battery when not said.
    const hasTrim = CATALOGUE_MODELS.some((m) => m.key === `${base.key}_DLX` || m.key === `${base.key}_ECO`);
    const said = /\b(ECO|DLX)\b/i.exec(`${batteryText ?? ''} ${text}`)?.[1]?.toUpperCase() ?? null;
    if (family === 'PRO') return product(family, base.key, base.label, said ?? proTrimOf(kind, batteryText));
    return product(family, base.key, base.label, hasTrim ? said : null);
  }
  const older = PRODUCTION_FAMILIES.find((f) => f.key === family);
  if (older) return product(family, older.key, titled(older.label), null);
  return product(OTHER_FAMILY, `AS_TYPED:${upper.replace(/\s+/g, ' ')}`, text.replace(/\s+/g, ' '), null);
}

/**
 * What the plan's words mean, said once and printed on the page and on the
 * paper alike (owner, 12 Oct 2026: "i need more explanation in report explain
 * varient properly").
 */
export const PRODUCTION_WORDS: ReadonlyArray<{ term: string; text: string }> = [
  {
    term: 'Model',
    text: 'The vehicle: CC Super, CC E-Max, CC Pro, and the cargo bodies (CC Jumbo, CC Mini Cargo, CC Dust Cart, CC Cargo Customised, CC Cargo Super Customised). The dispatch form calls it "Type of vehicle" (CITY CAB SUPER, CITY CAB E-MAX and so on), and the yard\'s stock carries those names.',
  },
  {
    term: 'Variant',
    text: 'Which version of the model to build, the dispatch form\'s "Type of model": ECO or DLX, and "Li" when it is built for a lithium battery. So DLX Li is the DLX for lithium, and DLX (lead acid) is the DLX for a lead-acid battery, which the dispatch form writes as plain DLX. The order form\'s "CC Super Eco" and "CC Super DLX" are one model, the Super, in two variants. The Pro\'s ECO or DLX is read from its battery: a 105 Ah battery is the ECO Li, a bigger one the DLX Li. The cargo bodies are not ordered as ECO or DLX, so their variant is the battery kind alone: Li or lead acid.',
  },
  {
    term: 'Battery size',
    text: '105 Ah, 132 Ah and so on is not a variant. The same variant goes out with different batteries, fitted at dispatch, so the size is listed beside each line for the battery store, never as a line of its own.',
  },
  {
    term: 'No battery',
    text: 'Ordered without a battery: the buyer fits their own. "Battery not said" means the order did not say which battery.',
  },
  {
    term: 'E-Max "+"',
    text: 'DLX+ Li and ECO+ Li are the E-Max with the 1650 W motor. The order form does not ask for it, so orders are not split by it; the yard\'s stock shows it where the dispatch form recorded it.',
  },
  {
    term: 'Stock',
    text: 'Only HEAD OFFICE is stock: vehicles built, checked and ready to send. The Production store lists chassis that are printed, not built (owner, 13 Oct 2026), so it is shown for the chassis in hand and never counted against an order.',
  },
  {
    term: 'Loader',
    text: 'The CC Jumbo, CC Mini Cargo, CC Dust Cart and the customised cargo bodies are built on the CITY CAB PRO chassis now; the old CITY CAB CARGO chassis still in hand fills them too. So orders for them and for the CC Pro are counted against both chassis together, as one loader line.',
  },
  {
    term: 'Build next',
    text: 'What the orders still need beyond what stands free in HEAD OFFICE, plus one week of orders kept ready, so a new order never waits for a build. A week of orders is the last four weeks of orders, divided by four.',
  },
  {
    term: 'To cover',
    text: 'Vehicles the open orders still want that are not already set aside for them in the yard. A colour "Not said" means the order did not name one: ask the counter before painting.',
  },
  {
    term: 'Older names',
    text: 'Orders written on the old dispatch form in the first week of September can read "Cargo, body not said" (the form named the cargo chassis, not the body) or name a model the order form no longer sells: City Auto, Garib Rath, City Cab EV.',
  },
];

/**
 * A vehicle's variant as the dispatch form spells it, however the sheet
 * wrote it ("DLX LI", "Dlx Lithium", "DLX  Li" → "DLX Li"), in the plan's
 * words: the form's bare "DLX" is the lead-acid build, so it reads
 * "DLX (lead acid)" as the demand tables say it. A trim the form does not
 * offer stays as written, spaces tidied. Blank → null.
 */
export function stockTrimOf(variant: string | null | undefined): string | null {
  const tidy = (variant ?? '').trim().replace(/\s+/g, ' ');
  if (!tidy) return null;
  const fold = (text: string) => text.toUpperCase().replace(/LITHIUM|LION/g, 'LI').replace(/\+/g, 'PLUS').replace(/[^A-Z0-9]/g, '');
  const trim = DISPATCH_MODEL_TYPES.find((option) => fold(option) === fold(tidy));
  if (!trim) return tidy;
  return batteryKindOf(trim) === 'LEAD' ? `${trim} (lead acid)` : trim;
}

/** A colour as the page groups it: case folded, spaces tidied. Blank = not recorded. */
export function colourKey(text: string | null | undefined): string {
  return (text ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

/** Vehicles per colour; the empty key is "colour not said / not recorded". */
export type ColourCounts = Record<string, number>;

export interface ProductionDemandModel {
  /** The model without its trim ("CC_SUPER" — `orderProductOf`), or the order line as written when the catalogue does not know it. */
  key: string;
  /** "CC Super". */
  label: string;
  /** Open orders naming it. */
  orders: number;
  /** Vehicles still owed: asked, less what has gone. */
  owed: number;
  /** Of those, already held for the order in the yard. */
  held: number;
  /** Owed and not yet held, by colour. */
  toCover: ColourCounts;
  /** Vehicles still to cover by variant ("DLX Li": 5), counted as `toCover` is — 12 Oct 2026. */
  variants: Record<string, number>;
  /** Vehicles still to cover by battery size ("105 Ah", "No battery"). */
  batteries: Record<string, number>;
}

export interface ProductionFamilyRow {
  /** The family orders and stock are netted in: a chassis family, the Pro and the old Cargo together as LOADER (13 Oct 2026). */
  key: string;
  label: string;
  /** Vehicles owed on open orders for this family. */
  owed: number;
  /** Held for those orders already — they are covered. */
  held: number;
  /** Standing in the dispatch store, held for nobody — the only stock (owner, 13 Oct 2026). */
  free: number;
  /** Chassis in the PDI store: printed, not built, so never counted as stock. */
  awaitingPdi: number;
  /** owed − held − free, never below nought: what the orders need built now. */
  toBuild: number;
  /** Free in the dispatch store beyond what is owed, never below nought. */
  surplus: number;
  /** One week of orders: vehicles ordered in the last four weeks, divided by four. */
  weekOfOrders: number;
  /** toBuild plus a week of orders kept ready, less any surplus: owed − held + weekOfOrders − free, never below nought. */
  buildNext: number;
  models: ProductionDemandModel[];
  /** Free and awaiting-PDI stock by colour, where the book knows it. */
  stockColours: ColourCounts;
}

export interface ProductionStockRow {
  family: string;
  /** The chassis as the factory register names it ("CITY CAB SUPER"); the vehicles' own spelling where no family fits. */
  model: string;
  /** The variant ("DLX Li", "DLX (lead acid)" — `stockTrimOf`), or null when the book has none: factory stock, mostly. */
  variant: string | null;
  colours: ColourCounts;
  total: number;
  awaitingPdi: number;
  held: number;
}

/**
 * One line the factory builds next (owner, 12 Oct 2026: "which orders are
 * need to be produced … which model needs to be produced on priority"): a
 * model and its variant, its colours in the same row, with what is still
 * uncovered on open orders and how long the oldest of them has waited.
 * Ranked oldest wait first, then the most uncovered, then the fastest-moving.
 *
 * A line is a model · variant, not a model · battery · colour (owner, same
 * day: "we dont have that many model and varient") — see `orderProductOf`.
 */
export interface BuildLine {
  rank: number;
  family: string;
  modelKey: string;
  /** "CC Super". */
  model: string;
  variantKey: string;
  /** "DLX Li". */
  variant: string;
  /** Vehicles to cover by colour key ('' = not said). */
  colours: ColourCounts;
  /** Vehicles to cover by battery size ("105 Ah", "No battery") — fitted at dispatch, not built. */
  batteries: Record<string, number>;
  /** Vehicles owed on open orders and not yet held for them. */
  toCover: number;
  /** Open orders behind this line. */
  orders: number;
  /** The IST day the oldest of those orders was raised, "YYYY-MM-DD". */
  oldestRaisedOn: string;
  daysWaiting: number;
  /** Vehicles of this model · variant ordered in the last 90 days, and dispatched in them. */
  orderedLast90: number;
  dispatchedLast90: number;
  /** Free (unheld) stock of the whole family in the dispatch store — PDI chassis are not built (13 Oct 2026). */
  familyFree: number;
}

/** One open order the factory is building for. */
export interface OpenOrderLine {
  id: string;
  raisedOn: string;
  daysWaiting: number;
  book: string;
  /** Whose order: the counter's name, or the caller's name when they are not on the network yet. */
  forName: string;
  forKind: string;
  placedBy: string;
  asm: string | null;
  /** "CC Super". */
  model: string;
  /** "DLX Li". */
  variant: string;
  /** The battery's size: "105 Ah", "No battery", "Not said". */
  battery: string;
  /** The colours as the order wrote them. */
  colours: string;
  qty: number;
  gone: number;
  held: number;
  toCover: number;
}

/** What has been coming in, month by month — the last three IST months, oldest first. */
export interface DemandTrend {
  months: string[];
  /** Vehicles ordered, by model · variant; a model's variants together, the busiest model first. */
  models: Array<{ key: string; label: string; model: string; variant: string; units: number[] }>;
  /** Vehicles ordered, by colour key ('' = not said). */
  colours: Array<{ key: string; units: number[] }>;
  /** Vehicles ordered and vehicles dispatched, per month, all models. */
  ordered: number[];
  dispatched: number[];
}

/** One model · variant · colour the orders asked for, and how many (13 Oct 2026: "what model varient and colour is most asked"). */
export interface AskedLine {
  /** The netting family: SUPER, LOADER, EMAX… (the Pro and the old Cargo together as LOADER). */
  family: string;
  modelKey: string;
  /** "CC E-Max". */
  model: string;
  variantKey: string;
  /** "DLX Li". */
  variant: string;
  /** Colour key ('' = not said). */
  colour: string;
  units: number;
}

export interface ProductionPlan {
  totals: { openOrders: number; owed: number; held: number; free: number; awaitingPdi: number; toBuild: number; weekOfOrders: number; buildNext: number };
  families: ProductionFamilyRow[];
  /** Every colour that appears anywhere on the page, busiest first ('' last). */
  colours: string[];
  /** Autozone's stock, model · variant by colour. */
  stock: ProductionStockRow[];
  /** What to build next, in order (12 Oct 2026). */
  buildFirst: BuildLine[];
  /** Every open order with vehicles still owed, longest-waiting first. */
  openOrders: OpenOrderLine[];
  trend: DemandTrend;
  /** The vehicles ordered over the last `days` IST days, by model · variant · colour, the most asked first. */
  asked: { days: number; total: number; lines: AskedLine[] };
  asOf: string;
}

// ---------------------------------------------------------------------------
// The factory's sheet
// ---------------------------------------------------------------------------
//
// Owner, 13 Oct 2026: "amit will only production sheet not the whole
// production section he dosent need to know who is ordering which vehicle he
// just needs to seee which vehicles he should be producing next". The sheet
// is read off the plan and carries no order, customer, counter, ASM or
// person: only what to build — model, variant, colour, how many.

/** One thing to build: a model · variant · colour, how many for orders already waiting, how many to keep ready. */
export interface FactorySheetLine {
  family: string;
  familyLabel: string;
  /** "CC E-Max". */
  model: string;
  /** "DLX Li". */
  variant: string;
  /** Colour key ('' = the order did not say: ask the office before painting). */
  colour: string;
  /** For orders already waiting that the dispatch store cannot give. */
  now: number;
  /** A week of orders kept ready, in the colours asked most. */
  ahead: number;
  total: number;
}

export interface FactorySheet {
  totals: { buildNext: number; now: number; ahead: number; ready: number };
  families: Array<{ key: string; label: string; buildNext: number; now: number; ahead: number }>;
  lines: FactorySheetLine[];
  /** The most-asked model · variant · colour over the last `days` days — no order, no customer. */
  asked: { days: number; total: number; lines: Array<{ model: string; variant: string; colour: string; units: number }> };
  asOf: string;
}

/** `total` shared out over `weights` by largest remainder: whole vehicles that add up exactly, ties in key order. */
function shareOut(total: number, weights: Map<string, number>): Map<string, number> {
  const out = new Map<string, number>();
  const sum = [...weights.values()].reduce((a, b) => a + b, 0);
  if (total <= 0 || sum <= 0) return out;
  const raw = [...weights.entries()].filter(([, w]) => w > 0).map(([key, w]) => ({ key, exact: (total * w) / sum }));
  for (const r of raw) out.set(r.key, Math.floor(r.exact));
  let left = total - [...out.values()].reduce((a, b) => a + b, 0);
  const byRemainder = [...raw].sort((a, b) => b.exact - Math.floor(b.exact) - (a.exact - Math.floor(a.exact)) || a.key.localeCompare(b.key));
  for (const r of byRemainder) {
    if (left <= 0) break;
    out.set(r.key, (out.get(r.key) ?? 0) + 1);
    left -= 1;
  }
  for (const [key, n] of out) if (n <= 0) out.delete(key);
  return out;
}

/**
 * The factory's sheet, read off the plan (13 Oct 2026). Each family's "build
 * next" is shared out over model · variant · colour: the part the waiting
 * orders need now, in the colours those orders asked for; the week kept
 * ready, in the colours asked most over the last four weeks (a colour not
 * said is left out of that part — nobody paints stock a colour nobody named).
 */
export function factorySheetOf(plan: ProductionPlan): FactorySheet {
  const lines: FactorySheetLine[] = [];
  const families: FactorySheet['families'] = [];
  const keyOf = (model: string, variant: string, colour: string) => JSON.stringify([model, variant, colour]);
  for (const family of plan.families) {
    if (family.buildNext <= 0) continue;
    const now = family.toBuild;
    const ahead = family.buildNext - family.toBuild;
    const waiting = new Map<string, number>();
    for (const line of plan.buildFirst.filter((l) => l.family === family.key)) {
      for (const [colour, units] of Object.entries(line.colours)) {
        const key = keyOf(line.model, line.variant, colour);
        waiting.set(key, (waiting.get(key) ?? 0) + units);
      }
    }
    const askedAll = plan.asked.lines.filter((l) => l.family === family.key);
    const asked = new Map<string, number>();
    for (const l of askedAll.filter((x) => x.colour !== '').length ? askedAll.filter((x) => x.colour !== '') : askedAll) {
      const key = keyOf(l.model, l.variant, l.colour);
      asked.set(key, (asked.get(key) ?? 0) + l.units);
    }
    const nowShare = shareOut(now, waiting.size ? waiting : asked);
    const aheadShare = shareOut(ahead, asked.size ? asked : waiting);
    const keys = [...new Set([...nowShare.keys(), ...aheadShare.keys()])];
    const familyLines = keys.map((key) => {
      const [model, variant, colour] = JSON.parse(key) as [string, string, string];
      const n = nowShare.get(key) ?? 0;
      const a = aheadShare.get(key) ?? 0;
      return { family: family.key, familyLabel: family.label, model, variant, colour, now: n, ahead: a, total: n + a };
    });
    // Nothing to share by (no order and no recent ask names it): one line for the family as a whole.
    if (familyLines.length === 0) familyLines.push({ family: family.key, familyLabel: family.label, model: family.label, variant: '', colour: '', now, ahead, total: now + ahead });
    familyLines.sort(
      (a, b) =>
        b.now - a.now ||
        b.total - a.total ||
        a.model.localeCompare(b.model) ||
        a.variant.localeCompare(b.variant) ||
        Number(a.colour === '') - Number(b.colour === '') ||
        a.colour.localeCompare(b.colour),
    );
    lines.push(...familyLines);
    families.push({ key: family.key, label: family.label, buildNext: family.buildNext, now, ahead });
  }
  const sum = (pick: (f: (typeof families)[number]) => number) => families.reduce((a, f) => a + pick(f), 0);
  return {
    totals: { buildNext: sum((f) => f.buildNext), now: sum((f) => f.now), ahead: sum((f) => f.ahead), ready: plan.totals.free },
    families,
    lines,
    asked: {
      days: plan.asked.days,
      total: plan.asked.total,
      lines: plan.asked.lines.slice(0, 10).map(({ model, variant, colour, units }) => ({ model, variant, colour, units })),
    },
    asOf: plan.asOf,
  };
}

// ---------------------------------------------------------------------------
// AI Daily Production Planning (Claude-powered)
// ---------------------------------------------------------------------------

/** One production batch scheduled on a specific day by the AI planner. */
export interface ProductionBatch {
  model: string;
  variant: string;
  colour: string;
  battery: string;
  quantity: number;
  reason: string;
  orderIds: string[];
  fulfilledCustomers: string[];
}

/** One day's production schedule recommended by AI. */
export interface DailySchedule {
  dayIndex: number;
  date: string;
  dayName: string;
  targetOutput: number;
  focusTheme: string;
  batches: ProductionBatch[];
  lineNotes: string;
}

/** Order-wise fulfillment tracking predicted by the AI. */
export interface OrderFulfillmentPrediction {
  orderId: string;
  customerName: string;
  book: string;
  model: string;
  variant: string;
  colours: string;
  /** The order's own battery ("105 Ah", "No battery", "Not said") — read from the order, never from the model's answer. */
  battery: string;
  toCover: number;
  daysWaiting: number;
  scheduledDay: number | null;
  scheduledDate: string | null;
  status: 'SCHEDULED_FULL' | 'SCHEDULED_PARTIAL' | 'BACKLOG';
  notes: string;
}

/** Shopfloor insight, bottleneck notice or efficiency opportunity. */
export interface ProductionInsight {
  type: 'EFFICIENCY' | 'BOTTLENECK' | 'URGENT' | 'COLOR_RUN';
  title: string;
  message: string;
}

/** The complete AI daily production plan. */
export interface AiDailyProductionPlan {
  generatedAt: string;
  modelUsed: string;
  summary: string;
  dailyCapacity: number;
  totalVehiclesPlanned: number;
  totalDaysPlanned: number;
  dailySchedules: DailySchedule[];
  orderFulfillments: OrderFulfillmentPrediction[];
  factoryRecommendations: ProductionInsight[];
}

export interface AiProductionPredictRequest {
  apiKey?: string;
  dailyCapacity?: number;
  planningDays?: number;
  model?: string;
  focus?: 'BALANCED' | 'CLEAR_OLDEST_FIRST' | 'BATCH_BY_COLOR';
}

export interface AiProductionState {
  hasServerKey: boolean;
  lastPlan: AiDailyProductionPlan | null;
}

